/**
 * ProfileViewContext — shares the active "which hat am I wearing" view between
 * the control (the role pill in the TopBar) and the consumer (the Sidebar,
 * which filters its nav by the active view).
 *
 * The state lives in one place so switching a view in the top bar immediately
 * re-filters the sidebar. Presentation only — see useProfileView; the backend
 * still authorizes by the full JWT role set.
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { Role } from '@kantera/types';
import { useAuth } from '../hooks/useAuth';
import { useResolvedRoles } from '../hooks/useResolvedRoles';
import { useProfileView, type ProfileView, type ProfileViewId } from '../hooks/useProfileView';

interface ProfileViewContextValue {
  /**
   * True when NO provider is mounted and this is the crash-guard default.
   *
   * Consumers must be able to tell "the user chose the staff hat" apart from
   * "there is no hat information at all". Without this the Dashboard would read
   * the guard's `staff` as a deliberate choice and pin everyone to the club
   * console — a silent wrong answer, which is worse than the crash it prevents.
   */
  isFallback: boolean;
  activeView: ProfileView;
  availableViews: ProfileView[];
  setView: (id: ProfileViewId) => void;
  effectiveRoles: Role[];
}

const ProfileViewContext = createContext<ProfileViewContextValue | null>(null);

export function ProfileViewProvider({ children }: { children: React.ReactNode }) {
  const { tenant } = useAuth();
  // Same role set the dashboard uses. Feeding this only `tenant?.roles` while
  // the dashboard used the wider union let the two disagree about who you are.
  const roles = useResolvedRoles();
  const view = useProfileView(roles, tenant?.userId);
  const value = useMemo(() => ({ ...view, isFallback: false }), [view]);
  return <ProfileViewContext.Provider value={value}>{children}</ProfileViewContext.Provider>;
}

/**
 * Read the active profile view. The provider wraps the whole authenticated
 * Layout, so every real consumer (TopBar, Sidebar) sits inside it. This default
 * is a pure crash-guard for a consumer that renders before the provider mounts:
 * a single non-interactive staff view with no switch action. `effectiveRoles`
 * is empty here — a nav filtered on it shows nothing, which is the correct
 * fail-closed shape for a momentary pre-auth render (never the steady state).
 */
export function useProfileViewContext(): ProfileViewContextValue {
  const ctx = useContext(ProfileViewContext);
  if (ctx) return ctx;
  return {
    isFallback: true,
    activeView: { id: 'staff', labelKey: 'viewStaff', roles: [] },
    availableViews: [],
    setView: () => {},
    effectiveRoles: [],
  };
}
